import React from "react";

import { useMountEffect } from "../../../hooks/useMountEffect";
import { useAppDispatch } from "../../../store/reducers";
import {
    CSRF_HEADER,
    HttpError,
    fetchJson,
    getCSRFToken,
} from "../../../utils/ajax";
import { BooleanInput } from "../BooleanInput";
import { SelectInput } from "../SelectInput";
import { TextInput } from "../TextInput";
import { viewStackPop } from "../reducers";

type IValue = string | boolean | undefined;

type IValues<IFieldName extends string> = {
    [K in IFieldName]?: IValue;
};

type IErrors<IFieldName extends string> = {
    [K in IFieldName]?: string[];
};

interface IField<IFieldName extends string> {
    name: IFieldName;
    type: "hidden" | "boolean" | "select" | "phone" | "text" | "email";
    label?: string;
    placeholder?: string;
    options?: [string, string][];
    isRequired?: boolean;
}

interface IProps<IFieldName extends string, U> {
    fields: IField<IFieldName>[];
    initialValues: IValues<IFieldName>;
    url?: string;
    baseURL?: string;
    className?: string;
    submitLabel?: string;

    onSubmit?: (
        event: React.FormEvent<HTMLFormElement>,
        data: IValues<IFieldName>,
    ) => Promise<void>;
    onPostSave?: (saveResp: U) => Promise<void>;
}

interface IState<IFieldName extends string> {
    data: IValues<IFieldName>;
    errors: IErrors<IFieldName> & {
        non_field_errors?: string[];
    };
}

const getDefaultState = <T extends string>(
    fields: IField<T>[],
    initialValues: IValues<T>,
): IState<T> => {
    const state: IState<T> = {
        data: {},
        errors: {},
    };
    fields.forEach((field) => {
        let value: IValue = "";
        if (field.type === "select") {
            value = (field.options || [])[0][0];
        }
        if (field.type === "boolean") {
            value = false;
        }
        const val = initialValues[field.name];
        if (val !== undefined) {
            value = val;
        }
        state.data[field.name] = value;
    });
    return state;
};

export const FormView = <T extends string, U>(
    props: IProps<T, U>,
): React.ReactNode => {
    const dispatch = useAppDispatch();
    const [state, setState] = React.useState<IState<T>>(() =>
        getDefaultState(props.fields, props.initialValues),
    );

    const inputRefs = React.useRef<{
        [P in T]?: HTMLInputElement | HTMLSelectElement | null;
    }>({});

    useMountEffect(() => {
        // Auto focus on the first field
        const field = props.fields.find((f) => f.type !== "hidden");
        if (field) {
            const node = inputRefs.current[field.name];
            if (node) {
                node.focus();
            }
        }
    });

    const onFieldChange = (name: T, value: IValues<T>[T]): void => {
        setState((s) => ({
            ...s,
            data: {
                ...s.data,
                [name]: value,
            },
        }));
    };

    const onPostSave = async (saveResp: U): Promise<void> => {
        if (props.onPostSave) {
            return props.onPostSave(saveResp);
        }
        dispatch(viewStackPop());
    };

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        try {
            if (props.onSubmit) {
                return props.onSubmit(event, {
                    ...state.data,
                });
            }
            event.preventDefault();
            let url: string;
            let method: string;
            if (props.url) {
                url = props.url;
                method = "PUT";
            } else if (props.baseURL) {
                url = props.baseURL;
                method = "POST";
            } else {
                throw new Error("Both url and baseURL props are missing");
            }
            const resp = await fetchJson<U>(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    [CSRF_HEADER]: await getCSRFToken(),
                },
                body: JSON.stringify(state.data),
            });
            await onPostSave(resp);
        } catch (err) {
            if (err instanceof HttpError) {
                setState((s) => ({
                    ...s,
                    errors: err.body as IErrors<T> & {
                        non_field_errors?: string[];
                    },
                }));
            } else {
                throw err;
            }
        }
    };

    const buildFields = (): React.JSX.Element[] => {
        return props.fields.map((field) => {
            if (field.type === "boolean") {
                return (
                    <BooleanInput
                        key={field.name}
                        forwardRef={(r) => {
                            inputRefs.current[field.name] = r;
                        }}
                        value={!!state.data[field.name]}
                        errors={state.errors[field.name]}
                        onChange={onFieldChange}
                        label={field.label}
                        name={field.name}
                        isRequired={field.isRequired}
                    />
                );
            }
            if (field.type === "select") {
                return (
                    <SelectInput
                        key={field.name}
                        forwardRef={(r) => {
                            inputRefs.current[field.name] = r;
                        }}
                        value={`${state.data[field.name]}`}
                        errors={state.errors[field.name]}
                        onChange={onFieldChange}
                        label={field.label}
                        name={field.name}
                        options={field.options || []}
                        isRequired={field.isRequired}
                    />
                );
            }
            return (
                <TextInput
                    key={field.name}
                    forwardRef={(r) => {
                        inputRefs.current[field.name] = r;
                    }}
                    value={`${state.data[field.name]}`}
                    errors={state.errors[field.name]}
                    onChange={onFieldChange}
                    type={field.type}
                    label={field.label}
                    name={field.name}
                    placeholder={field.placeholder}
                    isRequired={field.isRequired}
                />
            );
        });
    };

    return (
        <div className={props.className || "csr-edit-form"}>
            <form onSubmit={onSubmit}>
                <ul className="error-list">
                    {(state.errors.non_field_errors || []).map((err, i) => (
                        <li key={i}>{err}</li>
                    ))}
                </ul>
                {buildFields()}
                <button type="submit">{props.submitLabel || "Save"}</button>
            </form>
        </div>
    );
};
